import { type MouseEvent } from 'react';
import { clsx } from 'clsx';
import { Bookmark } from '@hungryroot/icons';
import { TransparentButton } from '@hungryroot/ramen';
import { type IconButtonProps } from '@hungryroot/ramen';
import { useDeleteFavoriteProduct } from '../../hooks/useDeleteFavoriteProduct';
import { useIsFavoriteProduct } from '../../hooks/useGetFavoriteProducts';
import { usePostFavoriteProduct } from '../../hooks/usePostFavoriteProduct';
import { FavoriteGroceriesIcon } from './FavoriteGroceriesIcon';
import styles from './FavoriteGroceries.module.css';

type VariantProps =
  | {
      variant: 'PDP';
      iconButtonVariant?: Pick<
        IconButtonProps,
        'prominence' | 'style' | 'size'
      >;
    }
  | { variant?: 'card' };

type FavoriteGroceriesProps = {
  testid?: string;
  customerId: number;
  productId: number;
  productName: string;
  className?: string;
  segment: {
    location: 'card' | 'PDP';
  };
  onFavorite?: () => void;
} & VariantProps;

export function FavoriteGroceries(props: FavoriteGroceriesProps) {
  const {
    testid,
    customerId,
    productId,
    productName,
    segment,
    onFavorite,
    className,
  } = props;

  const { isFavorited, isFetching } = useIsFavoriteProduct(
    customerId,
    productId
  );

  const postFavoriteProduct = usePostFavoriteProduct();
  const deleteFavoriteProduct = useDeleteFavoriteProduct();

  const { isPending: isPendingPostFavoriteProduct } = postFavoriteProduct;
  const { isPending: isPendingDeleteFavoriteProduct } = deleteFavoriteProduct;

  if (isFavorited === undefined) {
    return null;
  }

  const isDisabled =
    isFetching ||
    isPendingPostFavoriteProduct ||
    isPendingDeleteFavoriteProduct;

  const handleClick = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    const mutation = {
      data: { customerId, productId, productName, segment },
    };

    if (isFavorited) {
      deleteFavoriteProduct.mutate(mutation);
    } else {
      onFavorite?.();
      postFavoriteProduct.mutate(mutation);
    }
  };

  const classNames = clsx(
    {
      [styles.inactive]: !isFavorited,
      [styles.selected]: isFavorited,
      [styles.disabled]: isDisabled,
    },
    className
  );

  const ariaLabel = `${isFavorited ? 'Remove' : 'Add'} ${productName} ${
    isFavorited ? 'from' : 'to'
  } favorites`;

  const iconProps = {
    ariaLabel,
    className: classNames,
    testid,
    onClick: handleClick,
    disabled: isDisabled,
  };

  if (props.variant === 'PDP') {
    return (
      <FavoriteGroceriesIcon variant={props.iconButtonVariant} {...iconProps} />
    );
  }

  return (
    <TransparentButton {...iconProps}>
      <Bookmark />
    </TransparentButton>
  );
}
